import { useEffect, useMemo, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { DirectoryOperationsService, parseDirectoryError } from './DirectoryOperations.service';
import {
  PENDING_TTL_MS,
  operationsStorageKey,
  readStoredOperations,
  updateStoredOperations,
} from './DirectoryOperations.storage';
import { tBulkOperation } from './DirectoryOperations.types';
import {
  isTerminal,
  outcomeUnknown,
  tDirectoryOperationsOptions,
  tTrackedOperation,
  undoOpen,
  useDirectoryOperations,
} from './useDirectoryOperations.hook';

/**
 * Recovery for Directory operations (F3), as a wrapper per the polling-hook
 * rule (docs/solutions/logic-errors/polling-hook-misses-completion-after-remount.md):
 * on mount, per workspace and actor,
 *
 * 1. every create or Undo whose answer was lost is replayed with its saved
 *    Idempotency-Key, which returns the operation it already created;
 * 2. the actor's operations come back: running ones (polled again) and
 *    finished ones still inside their Undo window.
 *
 * While that runs, confirm and Undo wait: a click in that window could
 * repeat an operation whose card is about to appear. A 404 or 503 from the
 * list means there is nothing to recover here (no operations, or directory.v1
 * is off for this workspace), not an error to show.
 */
export function useDirectoryOperationsWithRecovery(options: tDirectoryOperationsOptions) {
  const base = useDirectoryOperations(options);
  const { enabled, workspaceId, actorId } = options;
  const [isRecovering, setIsRecovering] = useState(() =>
    Boolean(enabled && workspaceId && actorId),
  );

  const latestRef = useRef({
    adopt: base.adoptOperations,
    defaultTitle: options.defaultTitle,
    finished: options.onOperationFinished,
  });
  latestRef.current = {
    adopt: base.adoptOperations,
    defaultTitle: options.defaultTitle,
    finished: options.onOperationFinished,
  };

  useEffect(() => {
    if (!enabled || !workspaceId || !actorId) {
      setIsRecovering(false);
      return undefined;
    }
    const storageKey = operationsStorageKey(workspaceId, actorId);
    let cancelled = false;
    setIsRecovering(true);

    (async () => {
      try {
        const now = Date.now();
        const pending = readStoredOperations(storageKey).pending.filter(
          (write) => now - write.at < PENDING_TTL_MS,
        );
        // A replay can answer an operation that already finished: the table must catch up.
        let finishedByReplay = false;
        for (const write of pending) {
          if (cancelled) return;
          try {
            const operation =
              write.kind === 'create'
                ? await DirectoryOperationsService.create(workspaceId, {
                    previewId: write.previewId,
                    idempotencyKey: write.key,
                  })
                : await DirectoryOperationsService.undo(workspaceId, {
                    operationId: write.operationId,
                    idempotencyKey: write.key,
                  });
            updateStoredOperations(storageKey, (state) => ({
              titles: { ...state.titles, [operation.id]: { title: write.title, at: Date.now() } },
              pending: state.pending.filter((entry) => entry.key !== write.key),
            }));
            if (isTerminal(operation)) finishedByReplay = true;
          } catch (err) {
            // An answer is final (e.g. the preview expired before the create
            // ever landed); without one, the next mount tries again.
            if (!outcomeUnknown(parseDirectoryError(err))) {
              updateStoredOperations(storageKey, (state) => ({
                ...state,
                pending: state.pending.filter((entry) => entry.key !== write.key),
              }));
            }
          }
        }
        if (cancelled) return;

        const page = await DirectoryOperationsService.list(workspaceId);
        if (cancelled) return;
        const { titles } = readStoredOperations(storageKey);
        const undone = new Set(page.operations.map((operation) => operation.undo_of));
        const listedAt = Date.now();
        const recovered: tTrackedOperation[] = page.operations
          .filter((operation) => !isTerminal(operation) || undoOpen(operation, listedAt))
          .sort(newestFirst)
          .map((operation) => ({
            operation,
            title: titles[operation.id]?.title ?? latestRef.current.defaultTitle(operation.action),
            // Its compensation already exists: Undo is not offered twice.
            ...(undone.has(operation.id) && { undo: { status: 'requested' as const, key: '' } }),
          }));
        latestRef.current.adopt(recovered);
        if (finishedByReplay) latestRef.current.finished();
      } catch {
        // Best effort: without recovery the Directory still works.
      } finally {
        if (!cancelled) setIsRecovering(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId, actorId]);

  const confirmOperation = useMemoizedFn(async () => {
    if (!isRecovering) await base.confirmOperation();
  });
  // While recovering, a move or an assignment stops on its preview instead
  // of running: its confirm button waits for recovery to finish.
  const submitOperation = useMemoizedFn(async (
    request: Parameters<typeof base.submitOperation>[0],
  ) => {
    if (isRecovering) await base.requestPreview(request);
    else await base.submitOperation(request);
  });
  const undoOperation = useMemoizedFn(async (operationId: string) => {
    if (!isRecovering) await base.undoOperation(operationId);
  });
  return useMemo(() => ({
    ...base,
    isRecovering,
    confirmOperation,
    submitOperation,
    undoOperation,
  }), [base, isRecovering, confirmOperation, submitOperation, undoOperation]);
}

function newestFirst(a: tBulkOperation, b: tBulkOperation): number {
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

export type tDirectoryOperations = ReturnType<typeof useDirectoryOperationsWithRecovery>;
